import {InputProvider} from 'components/common/CartItemBuilder/providers/InputProvider'
import {ReactNode, createContext, useContext, useMemo} from 'react'
import {Basic} from 'types'
import {StepCode} from '../FoldingProvider'
import {useAvailabilityManager} from './StepDataManagers/useAvailabilityManager'
import {useDateManager} from './StepDataManagers/useDateManager'
import {useGuestsManager} from './StepDataManagers/useGuestsManager'
import {useOptionManager} from './StepDataManagers/useOptionManager'
import {UserInputStepManager} from './StepDataManagers/useUserInputStepManager'

// import { useWhatChanged } from '@simbathesailor/use-what-changed';

type Props = {
  children: ReactNode
}

type StepDataContextValue = {
  availability: UserInputStepManager<string>
  guests: UserInputStepManager<Basic.UnitCounterMap>
  date: UserInputStepManager<string>
  option: UserInputStepManager<string>
}

const StepDataContext = createContext<StepDataContextValue | null>(null)

export const StepDataProvider = ({children}: Props) => {
  const cartItemContext = InputProvider.useContext()
  const availability = useAvailabilityManager(cartItemContext)
  const date = useDateManager(cartItemContext)
  const guests = useGuestsManager(cartItemContext)
  const option = useOptionManager(cartItemContext)

  const value = useMemo(() => {
    return {
      availability,
      guests,
      date,
      option,
    }
  }, [availability, guests, date, option])

  return <StepDataContext.Provider value={value}>{children}</StepDataContext.Provider>
}

const useStepDataContext = () => {
  const context = useContext(StepDataContext)

  if (!context) {
    throw new Error('Step data context is null')
  }

  return context
}

function useStepDataManager(
  stepCode: StepCode
): UserInputStepManager<Basic.UnitCounterMap> | UserInputStepManager<string>
function useStepDataManager(
  stepCode: StepCode | null
): UserInputStepManager<Basic.UnitCounterMap> | UserInputStepManager<string> | null
function useStepDataManager(
  stepCode: StepCode | null
): UserInputStepManager<Basic.UnitCounterMap> | UserInputStepManager<string> | null {
  const context = useStepDataContext()

  if (!stepCode) {
    return null
  }

  return context[stepCode]
}

StepDataProvider.useStepDataManager = useStepDataManager

StepDataProvider.useAvailabilityDataManager = () => {
  const context = useStepDataContext()
  return context['availability']
}

StepDataProvider.useGuestsDataManager = () => {
  const context = useStepDataContext()
  return context['guests']
}

StepDataProvider.useDateDataManager = () => {
  const context = useStepDataContext()
  return context['date']
}

StepDataProvider.useOptionDataManager = () => {
  const context = useStepDataContext()
  return context['option']
}
